import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ChevronDown, CornerDownRight, Plus, Pencil, Shapes } from 'lucide-react';
import type { Category, CategoryNode, CategoriesResponse, CategoryType } from '@lira/shared';
import { apiGet, apiPatch } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import { CategoryIcon, EmptyState, Skeleton } from '@/components/ui';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/layout/PageHeader';
import { CategoryFormModal } from './CategoryFormModal';

interface FormTarget {
  open: boolean;
  parent?: CategoryNode;
  category?: Category;
}

function TypeTab({
  active,
  accent,
  onClick,
  children,
}: {
  active: boolean;
  accent: 'expense' | 'income';
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'min-h-11 rounded-full px-5 text-sm font-semibold transition-colors',
        active
          ? accent === 'expense'
            ? 'bg-expense text-white'
            : 'bg-income text-white'
          : 'text-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function RowActions({ children }: { children: React.ReactNode }) {
  return <div className="flex shrink-0 items-center gap-1">{children}</div>;
}

function IconButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Pencil;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="grid size-11 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
    >
      <Icon className="size-4" />
    </button>
  );
}

function CategoryRow({
  category,
  child = false,
  onEdit,
  onAddSub,
}: {
  category: Category;
  child?: boolean;
  onEdit: (c: Category) => void;
  onAddSub?: (parent: Category) => void;
}) {
  const queryClient = useQueryClient();
  const archive = useMutation({
    mutationFn: () => apiPatch(`/categories/${category.id}/archive`, { archived: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast('Categoria arquivada.', 'success');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  return (
    <div className={cn('flex items-center gap-3 px-4 py-3', child && 'pl-10')}>
      {child && <CornerDownRight className="size-4 shrink-0 text-muted" aria-hidden />}
      <CategoryIcon icon={category.icon} color={category.color} size={child ? 30 : 36} />
      <span className="min-w-0 flex-1 truncate text-[15px] text-ink">
        {category.name}
        {category.isSystem && <span className="ml-0.5 text-muted" title="Categoria do sistema">*</span>}
      </span>

      {!category.isSystem && (
        <RowActions>
          {onAddSub && !child && (
            <IconButton icon={Plus} label="Adicionar subcategoria" onClick={() => onAddSub(category)} />
          )}
          <IconButton icon={Pencil} label="Editar" onClick={() => onEdit(category)} />
          <IconButton icon={Archive} label="Arquivar" onClick={() => archive.mutate()} />
        </RowActions>
      )}
    </div>
  );
}

export function CategoriesPage() {
  const [type, setType] = useState<CategoryType>('expense');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [form, setForm] = useState<FormTarget>({ open: false });

  const query = useQuery({
    queryKey: ['categories', type],
    queryFn: () => apiGet<CategoriesResponse>(`/categories?type=${type}`),
  });

  const categories = query.data?.categories ?? [];
  const isEmpty = query.isSuccess && categories.length === 0;

  const openNew = () => setForm({ open: true });
  const openEdit = (category: Category) => setForm({ open: true, category });
  const openAddSub = (parent: Category) =>
    setForm({ open: true, parent: parent as CategoryNode });
  const closeForm = () => setForm((f) => ({ ...f, open: false }));

  const accentBg = type === 'expense' ? 'bg-expense' : 'bg-income';

  return (
    <section className="space-y-6">
      <PageHeader
        title="Categorias"
        subtitle="Organize receitas e despesas sem perder a semântica financeira."
        actions={
          <button
            type="button"
            onClick={openNew}
            aria-label="Nova categoria"
            className={cn(
              'inline-flex min-h-11 items-center gap-2 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-white transition-opacity hover:opacity-90',
              accentBg,
            )}
          >
            <Plus className="size-5" />
            Nova
          </button>
        }
      />

      <div className="inline-flex rounded-full border border-border bg-surface p-0.5">
        <TypeTab active={type === 'expense'} accent="expense" onClick={() => setType('expense')}>
          Despesas
        </TypeTab>
        <TypeTab active={type === 'income'} accent="income" onClick={() => setType('income')}>
          Receitas
        </TypeTab>
      </div>

      {query.isLoading && (
        <div className="space-y-2 rounded-[var(--radius-panel)] bg-surface p-4 shadow-card">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="line" className="h-10" />
          ))}
        </div>
      )}

      {query.isError && (
        <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
          {errorMessage(query.error)}
        </p>
      )}

      {isEmpty && (
        <EmptyState
          icon={Shapes}
          title="Nenhuma categoria"
          description={`Crie categorias de ${type === 'expense' ? 'despesa' : 'receita'} para classificar seus lançamentos.`}
        >
          <button
            type="button"
            onClick={openNew}
            className={cn(
              'inline-flex h-11 items-center gap-2 rounded-full px-6 text-sm font-semibold uppercase tracking-wide text-white transition-opacity hover:opacity-90',
              accentBg,
            )}
          >
            <Plus className="size-5" />
            Nova categoria
          </button>
        </EmptyState>
      )}

      {categories.length > 0 && (
        <div className="divide-y divide-border overflow-hidden rounded-[var(--radius-panel)] bg-surface shadow-card">
          {categories.map((node) => {
            const open = expanded.has(node.id);
            return (
              <div key={node.id}>
                <div className="flex items-center">
                  {node.children.length > 0 && <button type="button" onClick={() => setExpanded((current) => { const next = new Set(current); if (next.has(node.id)) next.delete(node.id); else next.add(node.id); return next; })} aria-label={`${open ? 'Ocultar' : 'Mostrar'} subcategorias de ${node.name}`} aria-expanded={open} className="ml-3 grid size-9 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"><ChevronDown className={cn('size-4 transition-transform duration-200', open && 'rotate-180')} /></button>}
                  <div className="min-w-0 flex-1"><CategoryRow category={node} onEdit={openEdit} onAddSub={openAddSub} /></div>
                </div>
                <div className={cn('grid transition-[grid-template-rows] duration-200 ease-out', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}><div className="min-h-0 overflow-hidden">{node.children.map((childCat) => (<div key={childCat.id} className="border-t border-border/60"><CategoryRow category={childCat} child onEdit={openEdit} /></div>))}</div></div>
              </div>
            );
          })}
        </div>
      )}

      <CategoryFormModal
        open={form.open}
        onClose={closeForm}
        type={type}
        parent={form.parent}
        category={form.category}
      />
    </section>
  );
}
